/**
 * The ONLY file in this app that imports @supabase/supabase-js.
 *
 * Supabase is used for one thing: exchanging an email and password for a token.
 * Every byte of business data goes through our own API, so there is no `.from()`
 * call anywhere in this codebase. If you are reaching for the Supabase client to
 * read a row, you are on the wrong path.
 *
 * Audit: grep -rn "supabase" src --include=*.ts* should only match this file.
 */
import { createClient, type Session } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error('VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set. Copy .env.example to .env.');
}

const supabase = createClient(url, anonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: false },
});

/** Distinguishes "wrong password" from "Supabase is unreachable". */
export type SignInResult =
  | { ok: true; session: Session }
  | { ok: false; kind: 'credentials' | 'network'; message: string };

export async function signIn(email: string, password: string): Promise<SignInResult> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      // Supabase returns 400 for bad credentials. Anything else (429, 5xx) is
      // not the user's fault and should not read "check your password".
      const isCredentials = error.status === 400;
      return {
        ok: false,
        kind: isCredentials ? 'credentials' : 'network',
        message: isCredentials ? 'Incorrect email or password.' : error.message,
      };
    }
    if (!data.session) {
      return { ok: false, kind: 'credentials', message: 'Sign-in did not return a session.' };
    }
    return { ok: true, session: data.session };
  } catch (err) {
    return {
      ok: false,
      kind: 'network',
      message: err instanceof Error ? err.message : 'Could not reach the sign-in service.',
    };
  }
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}

export async function currentSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export async function accessToken(): Promise<string | undefined> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token;
}

export function onAuthChange(cb: (session: Session | null) => void): () => void {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => cb(session));
  return () => data.subscription.unsubscribe();
}
